import { ElementDefinition } from "@amzn/exs-elements/web-component";
import schema from "./config/settings-schema.json";
import { LumberjackPlugin } from "@amzn/exs-elements/plugins";
import { ExsElementsEspAdapterPlugin } from "@amzn/exs-esp-adapters/plugins";
import { SustainabilityMetricEmitter } from "../../../sdk/metrics";
import { logError, logWarn } from "../../../sdk/metrics/logger";
import {
  SUSTAINABILITY_ELEMENT,
  SUSTAINABILITY_SELECTORS,
  SUSTAINABILITY_CSS_CLASSES,
  DEFAULT_PRODUCT_ID,
  CUSTOMIZATION_SETTINGS,
  PRODUCT_ATTRIBUTES,
} from "../../../constants";
import {
  parseWebComponentAttributes,
  loadFontCSS,
  getFontName,
} from "./utils/definition-helpers";
import { hydrateApp } from "./utils/hydration-helper";
import { injectShadowDOMCSS } from "./utils/css-injection";

const prod = new URL(import.meta.url).hostname === "cdn.elements.amazon";
const tagName = SUSTAINABILITY_ELEMENT.tagName;

const definition: ElementDefinition = {
  // make sure tagName is same as the folder name
  tagName: tagName,
  schema,
  shadowMode: "closed",
  plugins: [
    ExsElementsEspAdapterPlugin(),
    LumberjackPlugin(prod ? "CPFElement" : "CPFElementGamma", {
      properties: (element) => {
        const href = new URL(window.location.href);
        return {
          appId: `${tagName}:targetId=${element.getSetting("targetId") || "none"}:${href.host}${href.pathname}`,
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
        };
      },
      dimensions: [
        {
          elementName: tagName,
        },
        {},
      ],
      consoleOnlyLogging: false,
      localHostLogging: false,
    }),
    {
      onConnected: (target) => {
        // Array to store cleanup functions for this web component instance
        const cleanupFunctions: (() => void)[] = [];

        const metricEmitter = new SustainabilityMetricEmitter();

        target.onRendered(async (event) => {
          const shadowRoot = event.detail.shadowRoot;

          // Parse web component attributes
          const component_attributes = parseWebComponentAttributes(target);

          const productId =
            component_attributes[PRODUCT_ATTRIBUTES.productSku] ||
            DEFAULT_PRODUCT_ID;

          try {
            // Track element load
            metricEmitter.trackLoad();

            // Check for log message from server and log it
            const rootElement = shadowRoot.getElementById("root");
            const logMessage = rootElement?.getAttribute("data-log-message");
            if (logMessage) {
              console.log(logMessage);
            }

            // Inject CSS styles into shadow DOM for badge styling
            injectShadowDOMCSS(shadowRoot);

            // Load font CSS into document head and store cleanup function
            try {
              await loadFontCSS(
                component_attributes[CUSTOMIZATION_SETTINGS.fontFamily],
              );
            } catch (error) {
              logWarn({
                message: `Font CSS failed to load from CDN, relying on fallback fonts: ${error}`,
                shouldLogToConsole: true,
              });
            }

            // Set font family CSS custom property on shadow root host element
            const fontName = getFontName(
              component_attributes[CUSTOMIZATION_SETTINGS.fontFamily],
            );
            const hostElement = shadowRoot.host as HTMLElement;
            hostElement.style.setProperty(
              SUSTAINABILITY_CSS_CLASSES.fontFamilyProperty,
              `"${fontName}", "Amazon Ember", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
            );

            //Web component gets its own isolated React hydration in its shadow DOM
            const result = await hydrateApp(
              shadowRoot,
              SUSTAINABILITY_SELECTORS.root,
              component_attributes,
            );

            // Store cleanup function from hydration result if available
            if (result?.root) {
              cleanupFunctions.push(() => {
                // Clean up React root when web component is disconnected
                if (result.root.parentNode) {
                  result.root.innerHTML = "";
                }
              });
            }
            // Store metric emitter for component access
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (shadowRoot as any).__metricEmitter = metricEmitter;

            // Track render count
            metricEmitter.trackRender();
          } catch (error: unknown) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            logError({
              message: `Product ${productId} badge rendering failed: ${errorMessage}`,
              error,
              shouldLogToConsole: true,
            });

            const reasonCode =
              error instanceof Error && "code" in error && error.code
                ? String(error.code)
                : error instanceof Error
                  ? error.name || "UNKNOWN_ERROR"
                  : "UNKNOWN_ERROR";
            metricEmitter.trackError(reasonCode);

            throw error;
          }
        });

        // Return cleanup function that will be called when web component disconnects
        return () => {
          // Clean up resources when web component is removed from DOM
          cleanupFunctions.forEach((cleanup) => {
            try {
              cleanup();
            } catch (error) {
              logWarn({
                message: `Error during sustainability component cleanup: ${error}`,
                shouldLogToConsole: true,
              });
            }
          });
        };
      },
    },
  ],
};

// export default is required for definition.ts
export default definition;
